/**
 * ============================================================================
 * MASTER SERVICE: Account & Credentials Service (`tbl_user_accounts` - Sheet 7)
 * ============================================================================
 * Purpose:
 *   Handles API requests for tenant account management, API key rotation,
 *   credit top-ups, operator assignments, and secure credential views.
 *
 * Database Scope:
 *   Master Database (`digimaster`)
 * ============================================================================
 */

import api from "../../core/services/api";
import { createCrudService } from "../../core/services/crudServiceFactory";

const baseAccountService = createCrudService("accounts");

const accountService = {
  ...baseAccountService,

  /**
   * Fetches available operators and users for dropdown menus.
   *
   * @returns {Promise<Object>} { operators: Array, users: Array }
   */
  getAccountHelpers: () => {
    return api.get("/accounts/meta/helpers");
  },

  /**
   * Regenerates a new live API key for a specific account.
   *
   * @param {string|number} id - Account ID
   * @returns {Promise<Object>} Updated account with new str_api_key
   */
  regenerateApiKey: (id) => {
    return api.post(`/accounts/${id}/regenerate-key`);
  },

  /**
   * Adds / recharges credits for an account with an optional expiry date.
   *
   * @param {string|number} id - Account ID
   * @param {number} amount - Credit amount to add
   * @param {string|Date} [expire_on] - Expiration timestamp
   * @returns {Promise<Object>} Updated credit balance
   */
  addCredit: (id, amount, expire_on) => {
    return api.post(`/accounts/${id}/add-credit`, { amount, expire_on });
  },

  /**
   * Retrieves secure credentials and webhook URL for an account.
   *
   * @param {string|number} id - Account ID
   * @returns {Promise<Object>} Credential payload
   */
  getCredentials: (id) => {
    return api.get(`/accounts/${id}/credentials`);
  },
};

export default accountService;
